import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "btn",
    "tab",
    "cvLoader",
    "pdfViewer",
    "docxViewerContainer",
    "messageContainer",
    "standardiseTrigger",
  ];
  static values = {
    defaultTab: String,
    activeClass: String,
    memberId: String,
    inModal: Boolean,
    jobOpeningId: String,
    stage: String,
  };

  declare tabTargets: Array<HTMLElement>;
  declare btnTargets: Array<HTMLElement>;
  declare defaultTabValue: string;
  declare activeClassValue: string;
  declare memberIdValue: string;
  declare jobOpeningIdValue: string;
  declare inModalValue: boolean;
  declare stageValue: string;
  declare activeKlass: Array<string>;

  declare cvLoaderTarget: HTMLElement;
  declare pdfViewerTarget: HTMLIFrameElement;
  declare docxViewerContainerTarget: HTMLElement;
  declare messageContainerTarget: HTMLElement;
  declare standardiseTriggerTarget: HTMLButtonElement;

  declare hasCvLoaderTarget: boolean;
  declare hasStandardiseTriggerTarget: boolean;

  private cvLoaded = false;
  private boundRefreshModal: EventListener | null = null;

  initialize() {
    this.activeKlass = this.activeClassValue
      .split(",")
      .filter((x) => x)
      .concat(["active"]);
  }

  connect() {
    this.element.classList.add("tabs");
    this.tabTargets.forEach((tab) => (tab.hidden = true));

    const savedTabId =
      localStorage.getItem("selectedTab") || this.defaultTabValue;

    const selectedBtn = this.btnTargets.find(
      (element) => element.id === savedTabId
    );
    const selectedTab = this.tabTargets.find(
      (element) => element.id === savedTabId
    );

    if (selectedBtn && selectedTab) {
      selectedTab.hidden = false;
      selectedBtn.classList.add(...this.activeKlass);

      this.triggerLazyLoad(savedTabId);
    }

    this.boundRefreshModal = this.refreshModal.bind(this) as EventListener;
    window.addEventListener("weploy-cv:saved", this.boundRefreshModal);
  }

  disconnect() {
    if (this.boundRefreshModal) {
      window.removeEventListener("weploy-cv:saved", this.boundRefreshModal);
    }
  }

  select(event: Event) {
    const currentTarget = event.currentTarget as HTMLElement;
    const selectedTabId = currentTarget.id;

    const selectedTab = this.tabTargets.find(
      (element) => element.id === selectedTabId
    );

    if (selectedTab && selectedTab.hidden) {
      this.tabTargets.forEach((tab) => (tab.hidden = true));
      this.btnTargets.forEach((btn) =>
        btn.classList.remove(...this.activeKlass)
      );

      selectedTab.hidden = false;
      currentTarget.classList.add(...this.activeKlass);

      this.triggerLazyLoad(selectedTabId);
    }

    localStorage.setItem("selectedTab", selectedTabId);
  }

  async refreshModal(event: Event) {
    console.log("CV Saved Event Received.");

    if (!this.inModalValue) {
      console.log("Full page context detected. Reloading page...");
      window.location.reload();
      return;
    }

    console.log("Modal context detected. Fetching partial update...");

    const params = new URLSearchParams(window.location.search);

    if (this.jobOpeningIdValue) {
      params.set("job_opening_id", this.jobOpeningIdValue);
    }
    if (this.stageValue) {
      params.set("stage", this.stageValue);
    }

    const url = `/admin/community_members/${
      this.memberIdValue
    }/show_modal?${params.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      if (!response.ok) return;

      const data = await response.json();

      if (data.replace_content && data.replace_content["sidebar-container"]) {
        const sidebarContainer = document.getElementById("sidebar-container");
        if (sidebarContainer) {
          sidebarContainer.innerHTML =
            data.replace_content["sidebar-container"];
        } else {
          const sidebarWrapper =
            document.querySelector(".sidebar-panel") ||
            document.querySelector(".sidebar-wrapper");
          if (sidebarWrapper)
            sidebarWrapper.innerHTML =
              data.replace_content["sidebar-container"];
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  async openStandardiseModal(event: Event) {
    event.preventDefault();
    const button = event.currentTarget as HTMLButtonElement;
    const originalText = button.innerHTML;
    button.innerHTML = "Opening...";
    button.disabled = true;

    try {
      const response = await fetch(
        `/admin/community_members/${this.memberIdValue}/standardise_cv`
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      if (data.status === "success") {
        document.body.insertAdjacentHTML("beforeend", data.html);
      }
    } catch (error) {
      console.error("Error opening standardise modal:", error);
      alert("Cannot open Standardise Workspace. Please try again.");
    } finally {
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }

  closeStandardiseModal(event: Event) {
    const modal = document.getElementById("weploy-standardise-modal");
    if (modal) {
      modal.remove();
    }
  }

  private async triggerLazyLoad(tabId: string) {
    if (tabId !== "candidate_cv" || this.cvLoaded) {
      return;
    }

    if (!this.hasCvLoaderTarget) {
      return;
    }

    this.cvLoaded = true;
    this.showLoader();
    try {
      const response = await fetch(
        `/admin/community_members/${this.memberIdValue}/view_cv_inline`
      );
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      const data = await response.json();
      this.handleCvResponse(data);
    } catch (error) {
      console.error("Failed to fetch CV data:", error);
      this.showMessage("Connection error, cannot fetch CV data.");
    } finally {
      this.hideLoader();
    }
  }

  private handleCvResponse(data: any) {
    this.hideAllContainers();
    switch (data.status) {
      case "processing":
        this.docxViewerContainerTarget.innerHTML = `
          <div class="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200"
               data-controller="organization-app--cv-poller" 
               data-organization-app--cv-poller-url-value="${data.poll_url}"
               data-organization-app--cv-poller-interval-value="2000"
               data-organization-app--cv-poller-auto-refresh-value="true"> 
            
            <div class="relative mb-4">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <div class="absolute top-0 left-0 h-12 w-12 rounded-full border-2 border-primary-100 opacity-50"></div>
            </div>
            
            <h3 class="text-lg font-medium text-gray-900">AI is reading this CV...</h3>
            <p class="mt-1 text-sm text-gray-500">This typically takes about 10-30 seconds per CV.</p>
            <p class="text-xs text-gray-400 mt-2">You can navigate away, we'll keep working.</p>
          </div>
        `;
        this.docxViewerContainerTarget.classList.remove("hidden");
        break;
      case "success":
        if (data.file_type === "pdf") {
          this.pdfViewerTarget.src = data.url;
          this.pdfViewerTarget.classList.remove("hidden");
        } else if (data.file_type === "docx") {
          this.docxViewerContainerTarget.dataset.controller = "docx-viewer";
          this.docxViewerContainerTarget.dataset.docxViewerUrlValue = data.url;
          this.docxViewerContainerTarget.innerHTML = `
            <div data-docx-viewer-target="spinner" class="flex items-center justify-center h-full">
              <div class="text-center">
                <svg class="animate-spin mx-auto h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                <p class="mt-2 text-sm text-gray-500">DOCX processing...</p>
              </div>
            </div>
            <div data-docx-viewer-target="container"></div>
          `;
          this.docxViewerContainerTarget.classList.remove("hidden");
        } else if (data.file_type === "doc") {
          // Legacy binary .doc has no client-side viewer library (unlike
          // .docx/.pdf above) — the server extracts plain text instead
          // (data.url points to a JSON endpoint, not the raw file), so render
          // that as a simple read-only preview.
          this.docxViewerContainerTarget.innerHTML = `
            <div class="flex items-center justify-center h-full">
              <div class="text-center">
                <svg class="animate-spin mx-auto h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                <p class="mt-2 text-sm text-gray-500">Loading preview...</p>
              </div>
            </div>
          `;
          this.docxViewerContainerTarget.classList.remove("hidden");
          this.loadDocTextPreview(data.url);
        }
        break;
      case "empty":
        this.showMessage(data.message);
        // No CV was found for this candidate at all (as opposed to "error",
        // a transient lookup failure that might succeed on retry) — nothing
        // for Standardise to run against, so disable the trigger instead of
        // letting someone click it and hit a server-side error later.
        if (this.hasStandardiseTriggerTarget) {
          this.standardiseTriggerTarget.disabled = true;
          this.standardiseTriggerTarget.classList.add(
            "opacity-50",
            "cursor-not-allowed"
          );
          this.standardiseTriggerTarget.title =
            "No CV found for this candidate";
        }
        break;
      case "error":
        this.showMessage(data.message);
        break;
      default:
        this.showMessage("Invalid response from server.");
    }
  }

  private async loadDocTextPreview(url: string) {
    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Failed to load preview (${response.status})`);
      const data = await response.json();

      // Untrusted candidate-supplied content — set via textContent, never
      // interpolated into an HTML string, so it can't execute as markup.
      const pre = document.createElement("pre");
      pre.className =
        "whitespace-pre-wrap font-sans text-sm text-gray-800 p-6 overflow-auto h-full";
      pre.textContent = data.text;

      this.docxViewerContainerTarget.innerHTML = "";
      this.docxViewerContainerTarget.appendChild(pre);
    } catch (error) {
      console.error("Failed to load .doc preview:", error);
      this.docxViewerContainerTarget.innerHTML = `
        <div class="flex items-center justify-center h-full text-center p-6">
          <p class="text-sm text-gray-500">Preview isn't available for this older .doc format, but standardisation works normally — use Standardise Weploy CV below.</p>
        </div>
      `;
    }
  }

  private showMessage(message: string) {
    this.messageContainerTarget.innerHTML = `<p>${message}</p>`;
    this.messageContainerTarget.classList.remove("hidden");
  }

  private hideAllContainers() {
    this.pdfViewerTarget.classList.add("hidden");
    this.docxViewerContainerTarget.classList.add("hidden");
    this.messageContainerTarget.classList.add("hidden");
  }

  private showLoader() {
    this.cvLoaderTarget.classList.remove("hidden");
  }
  private hideLoader() {
    this.cvLoaderTarget.classList.add("hidden");
  }
}
